import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BudgetSummary } from "@/components/marketing/budgeting/BudgetSummary";
import { BudgetAllocation } from "@/components/marketing/budgeting/BudgetAllocation";
import { BudgetForm } from "@/components/marketing/budgeting/BudgetForm";
import { useBudgetData } from "@/components/marketing/budgeting/useBudgetData";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChartPie, DollarSign, BarChart3, Calendar, Edit, AlertCircle, RotateCcw } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";

export const BudgetingTab: React.FC = () => {
  const {
    loading,
    error,
    totalBudget,
    buckets,
    setTotalBudget,
    addBucket,
    updateBucket,
    deleteBucket,
    addSubcategory,
    updateSubcategory,
    deleteSubcategory,
    saveOverallBudget
  } = useBudgetData();
  const [activeView, setActiveView] = useState<string>("summary");
  const [showSetBudgetDialog, setShowSetBudgetDialog] = useState<boolean>(false);
  const [newTotalBudget, setNewTotalBudget] = useState<string>("");
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [isAlertError, setIsAlertError] = useState<boolean>(false);
  const [editBucketDialog, setEditBucketDialog] = useState<boolean>(false);
  const [selectedBucket, setSelectedBucket] = useState<string>("");
  const [editedBucketName, setEditedBucketName] = useState<string>("");
  const [editedBucketBudget, setEditedBucketBudget] = useState<string>("");
  const [editedBucketDescription, setEditedBucketDescription] = useState<string>("");
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);

  // Format input with commas as user types
  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-digit characters from input
    const value = e.target.value.replace(/[^\d]/g, '');

    // Format with commas
    if (value === '') {
      setNewTotalBudget('');
    } else {
      setNewTotalBudget(Number(value).toLocaleString('en-US'));
    }
  };
  const handleEditBudgetBucketChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-digit characters from input
    const value = e.target.value.replace(/[^\d]/g, '');

    // Format with commas
    if (value === '') {
      setEditedBucketBudget('');
    } else {
      setEditedBucketBudget(Number(value).toLocaleString('en-US'));
    }
  };
  const handleSaveOverallBudget = async () => {
    // Remove commas for processing
    const cleanValue = newTotalBudget.replace(/,/g, '');
    const budgetAmount = parseInt(cleanValue);
    if (isNaN(budgetAmount) || budgetAmount <= 0) {
      toast.error("Please enter a valid budget amount");
      return;
    }
    try {
      await saveOverallBudget(budgetAmount);
      setShowSetBudgetDialog(false);
      setNewTotalBudget("");

      // Show success alert
      setIsAlertError(false);
      setAlertMessage("Overall marketing budget saved successfully!");
      setShowAlert(true);
    } catch (err) {
      // Show error alert
      setIsAlertError(true);
      setAlertMessage(err instanceof Error ? err.message : "Failed to save budget. Please try again.");
      setShowAlert(true);
    }
  };
  const handleEditBucket = (bucketId: string) => {
    const bucket = buckets.find(b => b.id === bucketId);
    if (bucket) {
      setSelectedBucket(bucketId);
      setEditedBucketName(bucket.name);
      setEditedBucketBudget(bucket.budget.toLocaleString());
      setEditedBucketDescription(bucket.description || "");
      setEditBucketDialog(true);
    }
  };
  const handleSaveEditedBucket = async () => {
    if (!selectedBucket) return;

    // Remove commas for processing
    const cleanBudgetValue = editedBucketBudget.replace(/,/g, '');
    const budgetAmount = parseInt(cleanBudgetValue);
    if (isNaN(budgetAmount) || budgetAmount <= 0) {
      toast.error("Please enter a valid budget amount");
      return;
    }
    try {
      await updateBucket(selectedBucket, editedBucketName, budgetAmount, editedBucketDescription);
      setEditBucketDialog(false);
      toast.success("Budget bucket updated successfully!");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update budget bucket. Please try again.");
    }
  };

  const handleResetBudget = async () => {
    setIsResetting(true);
    try {
      const { data } = await supabase.auth.getUser();
      if (!data?.user) {
        toast.error("You need to be logged in to reset budget data");
        return;
      }

      const { error } = await supabase
        .from('marketing_budget')
        .delete()
        .eq('user_id', data.user.id);

      if (error) throw error;

      // Reset local state completely
      setTotalBudget(0);
      
      setShowResetConfirmDialog(false);
      
      // Show success alert
      setIsAlertError(false);
      setAlertMessage("Marketing budget has been reset successfully! You can now create a new marketing budget.");
      setShowAlert(true);
    } catch (err) {
      console.error('Error resetting budget:', err);
      // Show error alert
      setIsAlertError(true);
      setAlertMessage(err instanceof Error ? err.message : "Failed to reset budget. Please try again.");
      setShowAlert(true);
    } finally {
      setIsResetting(false);
      setShowResetConfirmDialog(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>;
  }
  if (error) {
    return <Alert variant="destructive" className="mb-4">
        <AlertDescription>
          Error loading budget data. Please try again later.
        </AlertDescription>
      </Alert>;
  }

  // Sort buckets alphabetically
  const sortedBuckets = [...buckets].sort((a, b) => a.name.localeCompare(b.name));
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Marketing Budget Dashboard</h2>
        </div>
        <div className="flex items-center space-x-2">
          {totalBudget > 0 ? <div className="bg-secondary p-2 rounded-md">
              <span className="text-primary text-sm font-medium">Total Budget:</span>{" "}
              <span className="text-white font-bold">${totalBudget?.toLocaleString() || '0'}</span>
            </div> : <div className="bg-secondary p-2 rounded-md">
              <span className="text-amber-400 text-sm font-medium">No budget set</span>
            </div>}
          <Dialog open={showSetBudgetDialog} onOpenChange={setShowSetBudgetDialog}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-primary text-black">
                {totalBudget ? 'Update Budget' : 'Set Budget'}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white text-black">
              <DialogHeader className="bg-white text-black">
                <DialogTitle className="text-black">{totalBudget ? 'Update Overall Marketing Budget' : 'Set Overall Marketing Budget'}</DialogTitle>
                <DialogDescription className="bg-white text-black">
                  Enter the total allocated budget for your marketing activities
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 text-black rounded-none">
                <div className="space-y-2">
                  <Label htmlFor="totalBudget" className="text-black">Total Budget ($)</Label>
                  <Input className="bg-white text-black" id="totalBudget" type="text" placeholder="e.g. 5,000,000" value={newTotalBudget} onChange={handleBudgetChange} />
                </div>
              </div>
              <DialogFooter className="">
                <Button variant="outline" onClick={() => setShowSetBudgetDialog(false)} className="rounded border-primary bg-white text-black">
                  Cancel
                </Button>
                <Button onClick={handleSaveOverallBudget}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {totalBudget > 0 && (
            <Button  
              className="gap-2 bg-red-500 text-black hover:bg-red-400"
              onClick={() => setShowResetConfirmDialog(true)}
              disabled={isResetting}
            >
              {isResetting ? 'Resetting...' : 'Reset Budget'}
            </Button>
          )}
        </div>
      </div>

      {/* Alert Dialog for success/error messages */}
      <AlertDialog open={showAlert} onOpenChange={setShowAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isAlertError ? "text-destructive" : "text-green-600"}>
              {isAlertError ? "Error" : "Success"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {alertMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowAlert(false)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Budget Confirmation Dialog */}
      <AlertDialog open={showResetConfirmDialog} onOpenChange={setShowResetConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2 bg-white text-black">
              <AlertCircle className="h-5 w-5" />
              Reset Marketing Budget
            </AlertDialogTitle>
            <AlertDialogDescription className="bg-white text-black">
              Are you sure you want to reset your entire marketing budget? This will permanently delete all budget data including:
              <br />• Overall marketing budget
              <br />• All budget buckets and allocations
              <br />• All subcategories and their details
              <br /><br />
              <strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-black" onClick={() => setShowResetConfirmDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetBudget}
              className="bg-destructive gap-2 bg-red-500 text-black hover:bg-red-400"
              disabled={isResetting}
            >
              {isResetting ? 'Resetting...' : 'Reset Budget'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Budget Bucket Dialog */}
      <Dialog open={editBucketDialog} onOpenChange={setEditBucketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-black">Edit Budget Bucket</DialogTitle>
            <DialogDescription className="text-black">
              Update the details for this marketing budget category
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 text-black">
            <div className="space-y-2">
              <Label htmlFor="bucketName" className="text-black">Category Name</Label>
              <Input id="bucketName" type="text" placeholder="e.g. Digital Marketing" value={editedBucketName} onChange={e => setEditedBucketName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bucketBudget" className="text-black">Budget Amount ($)</Label>
              <Input id="bucketBudget" type="text" placeholder="e.g. 100,000" value={editedBucketBudget} onChange={handleEditBudgetBucketChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bucketDescription" className="text-black">Description</Label>
              <Input id="bucketDescription" type="text" placeholder="e.g. Budget for all digital marketing activities" value={editedBucketDescription} onChange={e => setEditedBucketDescription(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBucketDialog(false)} className="rounded border-white">
              Cancel
            </Button>
            <Button onClick={handleSaveEditedBucket}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!totalBudget && <div className="bg-secondary/20 border-2 border-primary p-6 rounded-lg mb-6 text-center">
          <h3 className="text-xl font-medium text-white mb-3">Welcome to the Marketing Budget Dashboard</h3>
          <p className="text-white mb-4">No marketing budget has been set yet. Click the button below to set your overall marketing budget and start managing your allocations.</p>
          <Button onClick={() => setShowSetBudgetDialog(true)} className="gap-2">
            <DollarSign className="h-4 w-4" />
            Set Marketing Budget
          </Button>
        </div>}

      <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
        <TabsList className="grid grid-cols-3 mb-8 border border-primary/20 rounded">
          <TabsTrigger value="summary" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
            <ChartPie className="h-4 w-4 mr-1" />
            <span>Budget Summary</span>
          </TabsTrigger>
          <TabsTrigger value="manage" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
            <Calendar className="h-4 w-4 mr-1" />
            <span>Manage Main Buckets</span>
          </TabsTrigger>
          <TabsTrigger value="allocation" className="flex-1 text-primary data-[state=active]:bg-primary data-[state=active]:text-black data-[state=inactive]:bg-secondary data-[state=inactive]:text-white">
            <BarChart3 className="h-4 w-4 mr-1" />
            <span>Manage Sub Categories</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-0">
          <BudgetSummary totalBudget={totalBudget} buckets={buckets} />
        </TabsContent>

        <TabsContent value="manage" className="mt-0">
          <BudgetForm totalBudget={totalBudget} buckets={sortedBuckets} onAddBucket={addBucket} onUpdateBucket={updateBucket} onDeleteBucket={deleteBucket} onAddSubcategory={addSubcategory} onUpdateSubcategory={updateSubcategory} onDeleteSubcategory={deleteSubcategory} />
        </TabsContent>

        <TabsContent value="allocation" className="mt-0">
          <BudgetAllocation totalBudget={totalBudget} buckets={buckets} onAddSubcategory={addSubcategory} onUpdateSubcategory={updateSubcategory} onDeleteSubcategory={deleteSubcategory} />
        </TabsContent>
      </Tabs>
    </div>;
};
